import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  StackingColumnSeries,
  SplineSeries,
  Category,
  DataLabel,
  Legend,
  Tooltip,
  MultiLevelLabel,
  StripLine,
  IAxisLabelRenderEventArgs,
} from '@syncfusion/ej2-react-charts';

interface DateField {
  field: string;
  dayName: string;
}

interface DateShiftKpiChartProps {
  rawTradeData: Array<any>;
  dateFields: DateField[];
  height?: string;
}

const SHIFT_DAY = 'DS';
const SHIFT_NIGHT = 'NS';
const DS_COLOR = '#22d3ee';
const NS_COLOR = '#888888';

const loadLegendItems = [
  { label: '> 100%', color: '#C90016' },
  { label: '80% - 100%', color: '#008000' },
  { label: '< 80%', color: '#0D6EFD' },
];

const formatShortDate = (field: string) => {
  const d = new Date(field);
  if (isNaN(d.getTime())) return field;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const DateShiftKpiChart: React.FC<DateShiftKpiChartProps> = ({
  rawTradeData,
  dateFields,
  height = '320',
}) => {
const SHIFT_SPACER = 'SPACER';

  const { chartData, multiLevelCategories } = useMemo(() => {
    const data: Array<{
      x: string;
      shift: string;
      load: number;
      capacity: number;
      available: number;
      scaledLoad: number;
      scaledAvailable: number;
      utilisation: number;
      utilisationTooltip: string;
      loadColor: string;
    }> = [];

    const categories: Array<{ start: number; end: number; text: string; maxWidth: number }> = [];

    dateFields.forEach((df, dateIndex) => {
  const base = dateIndex * 3;
  categories.push({
    start: base,
    end: base + 1,
    text: formatShortDate(df.field),
    maxWidth: 100,
  });

  const dObj = new Date(df.field);
  const formattedDate = !isNaN(dObj.getTime())
    ? `${String(dObj.getDate()).padStart(2, '0')}/${String(dObj.getMonth() + 1).padStart(2, '0')}/${dObj.getFullYear()}`
    : df.field;

  (([SHIFT_DAY, SHIFT_NIGHT] as const)).forEach((shift) => {
    let load = 0;
    let capacity = 0;

    rawTradeData.forEach((trade) => {
      const bucket = trade[`${df.field}_${shift}data`];
      if (bucket) {
        load += Number(bucket.load) || 0;
        capacity += Number(bucket.capacity) || 0;
      }
    });

    const utilisation = capacity > 0 ? Math.round((load / capacity) * 100) : 0;
    const scale = capacity > 0 ? 10 / capacity : 0;
    const scaledLoad = load * scale;
    const available = Math.max(capacity - load, 0);
    const scaledAvailable = available * scale;
    const loadColor =
      utilisation > 100 ? '#C90016' : utilisation >= 80 ? '#008000' : '#0D6EFD';

    data.push({
      x: `${shift} || ${formattedDate}`, 
      shift,
      load,
      capacity,
      available,
      scaledLoad,
      scaledAvailable,
      utilisation,
      utilisationTooltip: `${utilisation}%`,
      loadColor,
    });
  });

  data.push({
    x: `${SHIFT_SPACER} || ${formattedDate}`,
    shift: SHIFT_SPACER,
    load: 0,
    capacity: 0,
    available: 0,
    scaledLoad: 0,
    scaledAvailable: 0,
    utilisation: 0,
    utilisationTooltip: '',
    loadColor: 'transparent',
  });
});

    return { chartData: data, multiLevelCategories: categories };
  }, [rawTradeData, dateFields]);

  const dsLineData = useMemo(
    () => chartData.filter((d) => d.shift === SHIFT_DAY),
    [chartData]
  );
  const nsLineData = useMemo(
    () => chartData.filter((d) => d.shift === SHIFT_NIGHT),
    [chartData]
  );

  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isLoadSeriesVisible, setIsLoadSeriesVisible] = useState(true);

  useEffect(() => {
  if (!containerRef.current) return;
  const observer = new ResizeObserver(() => {
    chartRef.current?.refresh();
    requestAnimationFrame(applyLegendStyles);
  });
  observer.observe(containerRef.current);
  return () => observer.disconnect();
}, []);

const handleAxisLabelRender = (args: IAxisLabelRenderEventArgs) => {
  if ((args.axis as any).name === 'primaryXAxis') {
    const parts = args.text.split('||');
    if (parts.length > 1) {
      const shift = parts[0].trim();
      args.text = shift === SHIFT_SPACER ? '' : shift;
    }
  }
};

  if (!rawTradeData.length || !dateFields.length) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#dcdcdc', fontSize: 14 }}>
        No data available for chart view.
      </div>
    );
  }

const originalTextColor = '#dcdcdc';
const disabledTextColor = '#999999';

const applyLegendStyles = () => {
  const chartId = 'date-shift-kpi-chart';
  const series = chartRef.current?.series;
  if (!series) return;

  series.forEach((s: any, index: number) => {
    const shapeEl = document.getElementById(`${chartId}_chart_legend_shape_${index}`);
    const textEl = document.getElementById(`${chartId}_chart_legend_text_${index}`);
    const isVisible = s.visible;

    if (shapeEl) {
      shapeEl.style.display = isVisible ? '' : 'none';
    }
    if (textEl) {
      textEl.setAttribute('fill', isVisible ? originalTextColor : disabledTextColor);
    }
    if (index === 0) {
      setIsLoadSeriesVisible(isVisible);
    }
  });
};

const handleLegendClick = () => {
  requestAnimationFrame(applyLegendStyles);
};



  return (
    <div ref={containerRef} style={{ marginTop: 10, padding: '0 0', position: 'relative' }}>
      {isLoadSeriesVisible && (
  <div
    style={{
      position: 'absolute',
      top: -3,
      right: 382,
      display: 'flex',
      gap: 16,
      alignItems: 'center',
      zIndex: 2,
    }}
  >
    {loadLegendItems.map((item) => (
      <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 1,
            backgroundColor: item.color,
            display: 'inline-block',
          }}
        />
        <span style={{ fontSize: 11, color: '#aaa' }}>{item.label}</span>
      </div>
    ))}
  </div>
)}
      <ChartComponent
        ref={chartRef}
        id='date-shift-kpi-chart'
        height={height}
        width='100%'
        margin={{ top: -10 }}
        background='transparent'
        legendClick={handleLegendClick}
        loaded={() => requestAnimationFrame(applyLegendStyles)}
        axisLabelRender={handleAxisLabelRender}
        primaryXAxis={{
          valueType: 'Category',
          labelRotation: 0,
          interval: 1,
          labelStyle: { size: '12px', color: '#aaa', fontWeight: '600' },
          labelPadding: 4,
          majorGridLines: { width: 0 },
          lineStyle: { color: '#aaa', },
          tickPosition: 'Outside',
          multiLevelLabels: [
            {
              border: { type: 'Brace', color: '#666' },
              overflow: 'Trim',
              
              categories: multiLevelCategories,
              textStyle: {
                color: '#dcdcdc',
                size: '12px',
                fontWeight: '500',
              },
            },
          ],
        }}
        primaryYAxis={{
          title: 'Load / Capacity (normalized)',
          minimum: 0,
          maximum: 12,
          interval: 2,
          titleStyle: { color: '#aaa', size: '13px', fontWeight: '500' },
          labelStyle: { color: '#aaa' },
          lineStyle: { color: '#444' },
          majorGridLines: { color: '#333', width: 1 },
        }}
        axes={[
          {
            name: 'secondaryYAxis',
            opposedPosition: true,
            minimum: 0,
            maximum: 120,
            interval: 20,
            title: 'Utilisation %',
            labelFormat: '{value}%',
            titleStyle: { color: '#22d3ee', size: '13px', fontWeight: '500' },
            labelStyle: { color: '#22d3ee' },
            lineStyle: { color: '#22d3ee', width: 1 },
            majorGridLines: { width: 0 },
            stripLines: [
              {
                start: 100,
                size: 1,
                sizeType: 'Pixel',
                dashArray: '4,4',
                color: '#dcdcdc',
                opacity: 0.8,
                zIndex: 'Over',
              },
            ],
          },
        ]}
        legendSettings={{
          visible: true,
          position: 'Top',
          alignment: 'Far',
          textStyle: { color: '#dcdcdc', size: '11px' },
        }}
        tooltip={{
          enable: true,
          shared: true,
          header: '${point.x}',
          format: '${series.name}: ${point.tooltip}',
          opacity: 0.85,
        }}
        chartArea={{ border: { width: 0 } }}
      >
        <Inject
          services={[
            StackingColumnSeries,
            SplineSeries,
            Category,
            DataLabel,
            Legend,
            Tooltip,
            MultiLevelLabel,
            StripLine,
          ]}
        />
        <SeriesCollectionDirective>
          <SeriesDirective
            dataSource={chartData}
            xName='x'
            yName='scaledLoad'
            type='StackingColumn'
            name='Sum of Load'
            fill='#15202b'
            pointColorMapping='loadColor'
            columnWidth={1}
            columnSpacing={0}
            border={{ width: 0 }}
            tooltipMappingName='load'
          />
          <SeriesDirective
            dataSource={chartData}
            xName='x'
            yName='scaledAvailable'
            type='StackingColumn'
            name='Available'
            fill='#33414b'
            opacity={0.9}
            columnWidth={1}
            columnSpacing={0}
            border={{ width: 0 }}
            tooltipMappingName='available'
          />
          <SeriesDirective
            dataSource={dsLineData}
            xName='x'
            yName='utilisation'
            type='Spline'
            splineType='Monotonic'
            name='DS Utilisation'
            dashArray='6,4'
            tooltipMappingName='utilisationTooltip'
            fill={DS_COLOR}
            width={2}
            yAxisName='secondaryYAxis'
            marker={{
              visible: true,
              width: 6,
              height: 6,
              fill: DS_COLOR,
              shape: 'Diamond',
              dataLabel: {
                visible: true,
                position: 'Top',
                font: { size: '11px', color: DS_COLOR, fontWeight: '600' },
                format: '{value}%',
              },
            }}
          />
          <SeriesDirective
            dataSource={nsLineData}
            xName='x'
            yName='utilisation'
            type='Spline'
            splineType='Monotonic'
            name='NS Utilisation'
            tooltipMappingName='utilisationTooltip'
            fill={NS_COLOR}
            width={2}
            dashArray='6,4'
            yAxisName='secondaryYAxis'
            marker={{
              visible: true,
              width: 6,
              height: 6,
              fill: NS_COLOR,
              shape: 'Diamond',
              dataLabel: {
                visible: true,
                position: 'Top',
                font: { size: '11px', color: NS_COLOR, fontWeight: '600' },
                format: '{value}%',
              },
            }}
          />
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  );
};

export default DateShiftKpiChart;